import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";

export const CloudProviderContext = createContext();

export const getAccountCloudType = (account) => {
  const type = account?.accounttype ?? account?.accountType;
  // Treat missing/null accountType as AWS (legacy accounts behave like AWS view)
  if (type == null || type === "null") {
    return "AWS";
  }
  return String(type).toUpperCase();
};

export const shouldShowAzurePlaceholderView = (selectedProvider) => {
  return selectedProvider === "AZURE";
};

export const CloudProviderProvider = ({ children }) => {
  const [selectedProvider, setSelectedProvider] = useState("AWS");
  // Accumulate providers only for the active org so provider-scoped account
  // fetches (Compliance/CSPM) do not hide the toggle mid-session.
  const [knownProviders, setKnownProviders] = useState([]);
  const activeOrgCodeRef = useRef(null);

  const organizations = useSelector(
    (state) => state.orgAccount?.organizations
  );
  const orgCode = organizations?.[0]?.orgcode ?? null;

  const resetCloudProviderState = useCallback(() => {
    setKnownProviders([]);
    setSelectedProvider("AWS");
  }, []);

  // Org switch / RESET_STORE: rebuild toggle state from the new org only
  useEffect(() => {
    if (!orgCode) {
      // Store reset or accounts not loaded yet — clear cross-org memory
      if (activeOrgCodeRef.current !== null) {
        resetCloudProviderState();
        activeOrgCodeRef.current = null;
      }
      return;
    }

    if (
      activeOrgCodeRef.current !== null &&
      activeOrgCodeRef.current !== orgCode
    ) {
      resetCloudProviderState();
    }
    activeOrgCodeRef.current = orgCode;
  }, [orgCode, resetCloudProviderState]);

  const registerObservedProviders = useCallback((accounts = []) => {
    const types = getDistinctExplicitCloudProviders(accounts);
    if (types.size === 0) return;
    setKnownProviders((prev) => {
      const next = new Set(prev);
      let changed = false;
      types.forEach((type) => {
        if (!next.has(type)) {
          next.add(type);
          changed = true;
        }
      });
      return changed ? Array.from(next) : prev;
    });
  }, []);

  const showCloudProviderToggle = knownProviders.length > 1;

  const value = useMemo(
    () => ({
      selectedProvider,
      setSelectedProvider,
      registerObservedProviders,
      resetCloudProviderState,
      showCloudProviderToggle,
      knownProviders,
    }),
    [
      selectedProvider,
      registerObservedProviders,
      resetCloudProviderState,
      showCloudProviderToggle,
      knownProviders,
    ]
  );

  return (
    <CloudProviderContext.Provider value={value}>
      {children}
    </CloudProviderContext.Provider>
  );
};

// Explicit type only — null/"null" ignored (for toggle visibility)
export const getExplicitAccountCloudType = (account) => {
  const type = account?.accounttype ?? account?.accountType;
  if (type == null || type === "null") {
    return null;
  }
  const normalized = String(type).toUpperCase();
  return normalized === "AWS" || normalized === "AZURE" ? normalized : null;
};

export const getDistinctExplicitCloudProviders = (accounts = []) => {
  const types = new Set();
  accounts.forEach((account) => {
    const type = getExplicitAccountCloudType(account);
    if (type) {
      types.add(type);
    }
  });
  return types;
};

export const shouldShowCloudProviderToggle = (accounts = []) => {
  return getDistinctExplicitCloudProviders(accounts).size > 1;
};

// When toggle is hidden: sole explicit type, or AWS if none / all null
export const getImplicitCloudProviderWhenToggleHidden = (accounts = []) => {
  const types = getDistinctExplicitCloudProviders(accounts);
  if (types.size === 1) {
    return [...types][0];
  }
  return "AWS";
};
